/**
 * MT5 Execution Bridge — user-specific AXEL signal routing to the trader's
 * own MT5 terminal via a lightweight EA. Mounted inside ProfilePanel.
 *
 * The user controls everything: connect MT5, choose Manual vs Auto, set
 * risk + limits, approve/cancel pending signals, and an emergency kill
 * switch. Nothing auto-trades unless the trader explicitly enables it.
 */
import React, { useCallback, useEffect, useState } from "react";

const T = {
  surf: "#0d1117", surf2: "#0a0e15", border: "#1c232f",
  white: "#e6e7ec", muted: "#878a91", gold: "#C9A227",
  green: "#22c55e", red: "#ef4444", blue: "#3b82f6", amber: "#f59e0b",
};
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function authHeaders() {
  const token = localStorage.getItem("flexx_access_token");
  const h = { "Content-Type": "application/json" };
  if (token) h["Authorization"] = `Bearer ${token}`;
  return h;
}
async function api(path, opts = {}) {
  const r = await fetch(`${API}${path}`, {
    credentials: "include", headers: authHeaders(), ...opts,
  });
  if (!r.ok) {
    let msg = `Request failed (${r.status})`;
    try { const j = await r.json(); msg = (typeof j.detail === "string" ? j.detail : msg); } catch {}
    throw new Error(msg);
  }
  return r.json();
}

const STATUS_BADGE = {
  queued:   { c: T.blue,  l: "QUEUED" },
  pending:  { c: T.amber, l: "PENDING" },
  sent:     { c: T.gold,  l: "SENT" },
  filled:   { c: T.green, l: "FILLED" },
  rejected: { c: T.red,   l: "REJECTED" },
  expired:  { c: T.muted, l: "EXPIRED" },
  cancelled:{ c: T.muted, l: "CANCELLED" },
};

function Badge({ status }) {
  const s = STATUS_BADGE[status] || { c: T.muted, l: (status || "").toUpperCase() };
  return (
    <span data-testid={`mt5-status-${status}`} style={{
      fontSize: 10, fontFamily: "monospace", letterSpacing: 1, color: s.c,
      border: `1px solid ${s.c}`, borderRadius: 4, padding: "2px 6px", whiteSpace: "nowrap",
    }}>{s.l}</span>
  );
}

const card = { background: T.surf, border: `1px solid ${T.border}`, borderRadius: 12, padding: 16 };
const label = { fontSize: 10, letterSpacing: 2, color: T.gold, fontFamily: "monospace", marginBottom: 10 };
const btn = (bg, fg = "#07090f") => ({
  background: bg, color: fg, border: "none", borderRadius: 8, padding: "9px 14px",
  fontSize: 12, fontWeight: 700, cursor: "pointer", letterSpacing: 0.5,
});
const ghostBtn = { background: "transparent", color: T.white, border: `1px solid ${T.border}`,
  borderRadius: 8, padding: "8px 12px", fontSize: 12, cursor: "pointer" };
const inp = { background: T.surf2, border: `1px solid ${T.border}`, borderRadius: 8,
  color: T.white, padding: "8px 10px", fontSize: 13, width: "100%" };

function Toggle({ on, onChange, testid, danger }) {
  return (
    <button data-testid={testid} onClick={() => onChange(!on)} style={{
      width: 46, height: 26, borderRadius: 20, border: "none", cursor: "pointer",
      background: on ? (danger ? T.red : T.green) : "#2a3240", position: "relative", transition: "background-color .2s",
    }}>
      <span style={{ position: "absolute", top: 3, left: on ? 23 : 3, width: 20, height: 20,
        borderRadius: "50%", background: "#fff", transition: "left .2s" }} />
    </button>
  );
}

export default function Mt5BridgePanel() {
  const [conn, setConn] = useState(null);
  const [status, setStatus] = useState(null);
  const [queue, setQueue] = useState([]);
  const [history, setHistory] = useState([]);
  const [newToken, setNewToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, s, q, h] = await Promise.all([
        api("/mt5/connection"), api("/mt5/status"),
        api("/mt5/queue"), api("/mt5/history?limit=25"),
      ]);
      setConn(c); setStatus(s); setQueue(q.signals || []); setHistory(h.signals || []);
    } catch (e) { setErr(e.message); }
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const save = async (patch) => {
    setBusy(true); setErr("");
    try {
      const c = await api("/mt5/connection", { method: "PUT", body: JSON.stringify(patch) });
      setConn(c);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const genToken = async () => {
    setBusy(true); setErr("");
    try {
      const r = await api("/mt5/connection/token", { method: "POST" });
      setNewToken(r.link_token); await load();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const disconnect = async () => {
    setBusy(true);
    try { await api("/mt5/connection", { method: "DELETE" }); setNewToken(""); await load(); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const act = async (qid, action) => {
    try { await api(`/mt5/queue/${qid}/${action}`, { method: "POST" }); await load(); }
    catch (e) { setErr(e.message); }
  };

  const copyToken = () => {
    navigator.clipboard?.writeText(newToken).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 1800);
    });
  };

  if (!conn) {
    return <div style={card} data-testid="mt5-bridge-loading"><div style={label}>MT5 CONNECTION</div>
      <div style={{ color: T.muted, fontSize: 13 }}>Loading…</div></div>;
  }

  const online = !!(status && status.connected);
  const toggleSym = (sym) => {
    const set = new Set(conn.allowed_symbols || []);
    set.has(sym) ? set.delete(sym) : set.add(sym);
    save({ allowed_symbols: [...set] });
  };
  const toggleSrc = (src) => {
    const set = new Set(conn.enabled_sources || []);
    set.has(src) ? set.delete(src) : set.add(src);
    save({ enabled_sources: [...set] });
  };
  const toggleSession = (s) => {
    const set = new Set(conn.session_filter || []);
    set.has(s) ? set.delete(s) : set.add(s);
    save({ session_filter: [...set] });
  };

  return (
    <div style={{ display: "grid", gap: 14 }} data-testid="mt5-bridge-panel">
      {/* Header + status */}
      <div style={{ ...card, borderLeft: `3px solid ${T.gold}` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <div style={label}>⚡ MT5 EXECUTION BRIDGE</div>
          <span data-testid="mt5-connection-indicator" style={{
            fontSize: 11, fontFamily: "monospace", color: online ? T.green : T.muted,
            border: `1px solid ${online ? T.green : T.border}`, borderRadius: 20, padding: "3px 10px",
          }}>
            <span style={{ display: "inline-block", width: 7, height: 7, borderRadius: "50%",
              background: online ? T.green : T.muted, marginRight: 6 }} />
            {online ? "EA CONNECTED" : "EA OFFLINE"}
          </span>
        </div>
        <div style={{ color: T.muted, fontSize: 12.5, lineHeight: 1.6, marginTop: 4 }}>
          AXEL routes <b style={{ color: T.white }}>only your own</b> signals to <b style={{ color: T.white }}>your</b> MT5
          terminal. You are in full control — trades execute only when you turn on Auto-Execution.
          Turn it off and every signal stays pending for manual review.
        </div>
        {online && status.account && (
          <div style={{ marginTop: 10, fontSize: 12, color: T.white, display: "flex", gap: 16, flexWrap: "wrap" }}>
            <span>Acct <b>{status.account.login}</b></span>
            <span>Bal <b>{status.account.balance} {status.account.currency}</b></span>
            <span>{status.account.broker}</span>
          </div>
        )}
      </div>

      {err && <div data-testid="mt5-error" style={{ ...card, borderColor: T.red, color: T.red, fontSize: 12 }}>{err}</div>}

      {/* Link token + EA download */}
      <div style={card}>
        <div style={label}>1 · LINK YOUR TERMINAL</div>
        {!conn.has_token ? (
          <button data-testid="mt5-generate-token" onClick={genToken} disabled={busy} style={btn(T.gold)}>
            GENERATE LINK TOKEN
          </button>
        ) : (
          <div style={{ display: "grid", gap: 10 }}>
            {newToken ? (
              <div style={{ background: T.surf2, border: `1px dashed ${T.gold}`, borderRadius: 8, padding: 10 }}>
                <div style={{ fontSize: 10, color: T.amber, marginBottom: 6 }}>COPY NOW — SHOWN ONCE</div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <code data-testid="mt5-token-value" style={{ fontSize: 11, color: T.white, wordBreak: "break-all", flex: 1 }}>{newToken}</code>
                  <button data-testid="mt5-copy-token" onClick={copyToken} style={btn(T.gold)}>{copied ? "✓ COPIED" : "COPY"}</button>
                </div>
              </div>
            ) : (
              <div style={{ fontSize: 12, color: T.muted }}>Token active: <code style={{ color: T.white }}>{conn.token_masked}</code></div>
            )}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button data-testid="mt5-rotate-token" onClick={genToken} disabled={busy} style={ghostBtn}>↻ Regenerate</button>
              <button data-testid="mt5-disconnect" onClick={disconnect} disabled={busy} style={{ ...ghostBtn, color: T.red, borderColor: T.red }}>Disconnect</button>
            </div>
          </div>
        )}
        <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap" }}>
          <a data-testid="mt5-download-ea" href="/flexx-ea/FlexxAxelBridge.mq5" download style={{ ...ghostBtn, textDecoration: "none", color: T.gold, borderColor: T.gold }}>⬇ Download EA (.mq5)</a>
          <a data-testid="mt5-download-readme" href="/flexx-ea/README.txt" target="_blank" rel="noreferrer" style={{ ...ghostBtn, textDecoration: "none" }}>Setup guide</a>
        </div>
      </div>

      {/* Execution mode + settings */}
      <div style={card}>
        <div style={label}>2 · TRADE EXECUTION MODE</div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: conn.auto_execute ? T.green : T.white }}>
              {conn.auto_execute ? "AUTO-EXECUTE" : "MANUAL"}
            </div>
            <div style={{ fontSize: 11.5, color: T.muted }}>
              {conn.auto_execute ? "Signals fire on your terminal automatically." : "Signals stay pending until you approve them."}
            </div>
          </div>
          <Toggle testid="mt5-auto-toggle" on={conn.auto_execute} onChange={(v) => save({ auto_execute: v })} />
        </div>

        <Row label="Manual approval before each auto-trade">
          <Toggle testid="mt5-approval-toggle" on={conn.manual_approval} onChange={(v) => save({ manual_approval: v })} />
        </Row>

        <div style={{ borderTop: `1px solid ${T.border}`, margin: "12px 0" }} />

        <div style={{ fontSize: 11, color: T.muted, marginBottom: 4 }}>SIGNAL SOURCES <span style={{ color: T.gold }}>· CFD DESK</span></div>
        <div style={{ fontSize: 10.5, color: T.muted, marginBottom: 8 }}>AXEL CFD Desk generates these for XAUUSD & BTCUSD.</div>
        <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
          {[["mt5_setup", "MT5 Setup"], ["live_trigger", "Live Trigger"]].map(([id, name]) => {
            const on = (conn.enabled_sources || []).includes(id);
            return <button key={id} data-testid={`mt5-src-${id}`} onClick={() => toggleSrc(id)}
              style={{ ...ghostBtn, borderColor: on ? T.gold : T.border, color: on ? T.gold : T.muted }}>{on ? "✓ " : ""}{name}</button>;
          })}
        </div>

        <div style={{ fontSize: 11, color: T.muted, marginBottom: 8 }}>ALLOWED SYMBOLS</div>
        <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
          {["XAUUSD", "BTCUSD"].map((s) => {
            const on = (conn.allowed_symbols || []).includes(s);
            return <button key={s} data-testid={`mt5-sym-${s}`} onClick={() => toggleSym(s)}
              style={{ ...ghostBtn, borderColor: on ? T.gold : T.border, color: on ? T.gold : T.muted }}>{on ? "✓ " : ""}{s}</button>;
          })}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label="Risk mode">
            <select data-testid="mt5-risk-mode" value={conn.risk_mode} onChange={(e) => save({ risk_mode: e.target.value })} style={inp}>
              <option value="fixed_lot">Fixed lot</option>
              <option value="risk_percent">Risk % / trade</option>
            </select>
          </Field>
          {conn.risk_mode === "fixed_lot" ? (
            <NumField label="Fixed lot size" testid="mt5-fixed-lot" value={conn.fixed_lot} step="0.01" onSave={(v) => save({ fixed_lot: v })} />
          ) : (
            <NumField label="Risk % per trade" testid="mt5-risk-pct" value={conn.risk_percent} step="0.1" onSave={(v) => save({ risk_percent: v })} />
          )}
          <NumField label="Max daily trades" testid="mt5-max-trades" value={conn.max_daily_trades} step="1" onSave={(v) => save({ max_daily_trades: Math.round(v) })} />
          <NumField label="Max daily drawdown %" testid="mt5-max-dd" value={conn.max_daily_drawdown_pct} step="0.5" onSave={(v) => save({ max_daily_drawdown_pct: v })} />
          <Field label="Broker symbol suffix">
            <input data-testid="mt5-suffix" defaultValue={conn.symbol_suffix} placeholder="e.g. .m" style={inp}
              onBlur={(e) => e.target.value !== conn.symbol_suffix && save({ symbol_suffix: e.target.value })} />
          </Field>
        </div>

        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 11, color: T.muted, marginBottom: 8 }}>
            SESSION FILTER <span style={{ color: T.muted, opacity: 0.7 }}>· none selected = all sessions allowed</span>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {[["ASIA", "Asia"], ["LONDON", "London"], ["LONDON_NY_OVERLAP", "LDN/NY Overlap"], ["NEWYORK", "New York"], ["AFTER_HOURS", "After Hours"]].map(([id, name]) => {
              const on = (conn.session_filter || []).includes(id);
              return <button key={id} data-testid={`mt5-session-${id}`} onClick={() => toggleSession(id)}
                style={{ ...ghostBtn, borderColor: on ? T.gold : T.border, color: on ? T.gold : T.muted }}>{on ? "✓ " : ""}{name}</button>;
            })}
          </div>
        </div>
      </div>

      {/* Kill switch */}
      <div style={{ ...card, borderColor: conn.kill_switch ? T.red : T.border }}>
        <Row label={<span><b style={{ color: conn.kill_switch ? T.red : T.white }}>🛑 EMERGENCY KILL SWITCH</b>
          <div style={{ fontSize: 11.5, color: T.muted, marginTop: 2 }}>Instantly stops all execution. No signals reach your terminal.</div></span>}>
          <Toggle testid="mt5-kill-toggle" danger on={conn.kill_switch} onChange={(v) => save({ kill_switch: v })} />
        </Row>
      </div>

      {/* Pending / active signals */}
      <div style={card}>
        <div style={label}>PENDING & ACTIVE SIGNALS</div>
        {queue.length === 0 ? (
          <div data-testid="mt5-queue-empty" style={{ color: T.muted, fontSize: 13 }}>No pending signals right now.</div>
        ) : (
          <div style={{ display: "grid", gap: 8 }} data-testid="mt5-queue-list">
            {queue.map((q) => (
              <div key={q.queue_id} data-testid={`mt5-queue-item-${q.queue_id}`} style={{
                background: T.surf2, border: `1px solid ${T.border}`, borderRadius: 8, padding: 10,
                display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <div style={{ fontSize: 13, color: T.white }}>
                  <b>{q.symbol}</b> {q.side} · {q.order_type} @ {q.entry ?? "mkt"}
                  <span style={{ color: T.muted, fontSize: 11, marginLeft: 8 }}>SL {q.stop_loss} · TP {q.take_profit} · {q.confidence}%</span>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <Badge status={q.status} />
                  {q.status === "pending" && <button data-testid={`mt5-approve-${q.queue_id}`} onClick={() => act(q.queue_id, "approve")} style={btn(T.green, "#fff")}>Approve</button>}
                  {(q.status === "pending" || q.status === "queued") && <button data-testid={`mt5-cancel-${q.queue_id}`} onClick={() => act(q.queue_id, "cancel")} style={ghostBtn}>Cancel</button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Execution history */}
      <div style={card}>
        <div style={label}>EXECUTION HISTORY</div>
        {history.length === 0 ? (
          <div data-testid="mt5-history-empty" style={{ color: T.muted, fontSize: 13 }}>No executions yet.</div>
        ) : (
          <div style={{ display: "grid", gap: 6 }} data-testid="mt5-history-list">
            {history.map((h) => (
              <div key={h.queue_id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                fontSize: 12.5, color: T.white, padding: "6px 0", borderBottom: `1px solid ${T.border}` }}>
                <span><b>{h.symbol}</b> {h.side} {h.order_type}{h.ticket ? ` · #${h.ticket}` : ""}
                  {h.reject_reason ? <span style={{ color: T.muted }}> · {h.reject_reason}</span> : ""}</span>
                <Badge status={h.status} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
    <div style={{ fontSize: 13, color: T.white }}>{label}</div>{children}</div>;
}
function Field({ label, children }) {
  return <div><div style={{ fontSize: 10.5, color: T.muted, marginBottom: 5 }}>{label}</div>{children}</div>;
}
function NumField({ label, value, step, onSave, testid }) {
  return <Field label={label}>
    <input data-testid={testid} type="number" step={step} defaultValue={value} style={inp}
      onBlur={(e) => { const v = parseFloat(e.target.value); if (!isNaN(v) && v !== value) onSave(v); }} />
  </Field>;
}
